// @ts-nocheck
import React, { useEffect, useRef, useCallback } from "react";
import { api } from "@/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useSocket } from "@/contexts/SocketContext";
import { useQueryClient } from "@tanstack/react-query";
import {
  MessageSquare, Users, CheckSquare, FileText, Calendar, Target, Bell, Mail, AlertCircle
} from "lucide-react";

const NOTIFICATION_ICONS = {
  new_message: MessageSquare,
  mention: Users,
  task_assigned: CheckSquare,
  form_assigned: FileText,
  review_scheduled: Calendar,
  appointment_reminder: Bell,
  goal_milestone: Target,
  campaign_enrolled: Mail,
  system_alert: AlertCircle
};

function getNotificationData(notification: any) {
  if (!notification?.data) return {};
  if (typeof notification.data === 'string') {
    try {
      return JSON.parse(notification.data);
    } catch {
      return {};
    }
  }
  return notification.data;
}

function getNotificationType(notification: any) {
  const notifData = getNotificationData(notification);
  if (notification?.type && notification.type !== 'info') return notification.type;
  if (notifData.type) return notifData.type;
  if (notifData.notification_type) return notifData.notification_type;
  if (
    notifData.related_entity_type === 'Note' &&
    typeof notification?.title === 'string' &&
    notification.title.toLowerCase().includes('mentioned')
  ) {
    return 'mention';
  }
  return notification?.type || 'info';
}

export default function NotificationToastListener({ userEmail, userId }) {
  const navigate = useNavigate();
  const { on, off, connected } = useSocket();
  const queryClient = useQueryClient();
  const shownNotificationIds = useRef(new Set());

  const showNotificationToast = useCallback((notification: any) => {
    if (!notification || shownNotificationIds.current.has(notification.id)) return;
    if (notification.userId && userId && notification.userId !== userId) return;

    // Immediately invalidate the notifications query so bell dropdown & page refresh
    queryClient.invalidateQueries({ queryKey: ['notifications'] });

    const notifData = getNotificationData(notification);
    const notificationType = getNotificationType(notification);
    const Icon = NOTIFICATION_ICONS[notificationType] || Bell;
    toast(notification.title, {
      description: notification.message,
      icon: <Icon className="w-5 h-5" />,
      action: notifData.action_url ? {
        label: notifData.action_label || 'View',
        onClick: () => {
          navigate(notifData.action_url);
        }
      } : undefined,
      duration: 8000,
    });
    shownNotificationIds.current.add(notification.id);
  }, [navigate, queryClient, userId]);

  // Real-time notifications via Socket.IO
  useEffect(() => {
    if (!userEmail) return;

    on('new-notification', showNotificationToast);
    return () => off('new-notification', showNotificationToast);
  }, [userEmail, on, off, showNotificationToast]);

  // Fallback polling when socket is disconnected
  useEffect(() => {
    if (!userEmail || connected) return;

    const lastChecked = { current: new Date() };

    const interval = setInterval(async () => {
      try {
        // Invalidate query to force refetch (the useNotifications hook handles the actual fetch)
        queryClient.invalidateQueries({ queryKey: ['notifications'] });
      } catch (err) {
        // Silently ignore polling errors
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [userEmail, connected, queryClient]);

  return null;
}